export type RcData = {
  document_type: string;
  document_id: string;
  owner_data: {
    serial: string;
    name: string;
    name_match_score_1?: string;
    name_match_score_2?: string;
    present_address: string;
    permanent_address: string;
  };
  issue_date: string;
  expiry_date: string;
  registered_at: string;
  status: string;
  norms_type: string;
  tax_end_date: string;
  financier?: string;
  financed: boolean;
  is_commercial: boolean;
  pucc_data: {
    pucc_number: string;
    expiry_date: string;
  };
  insurance_data: {
    policy_number: string;
    company: string;
    expiry_date: string;
  };
  vehicle_data: {
    manufactured_date: string;
    category: string;
    category_description: string;
    chassis_number: string;
    engine_number: string;
    maker_description: string;
    maker_model: string;
    body_type: string;
    fuel_type: string;
    color: string;
    cubic_capacity: string;
    gross_weight: string;
    number_of_cylinders: string;
    seating_capacity: string;
    sleeper_capacity: string;
    standing_capacity: string;
    wheelbase: string;
    unladen_weight: string;
  };
};

// Insurance details fetched via Account Aggregator (FI_TYPE = GENERAL_INSURANCE).
// Once user consents, we pull premium / IDV / NCB / add-ons / claim history.
export type InsuranceAAData = {
  linked: boolean;
  premium?: number;
  idv?: number;
  ncb?: number; // percent
  addOns?: string[];
  lastClaim?: {
    date: string;
    amount: number;
    status: 'SETTLED' | 'PENDING' | 'REJECTED';
  };
  claimSettlementRatio?: number;
};

// Loan info fetched via Account Aggregator framework (FI_TYPE = TERM_LOAN).
export type LoanData = {
  linked: boolean;
  lender?: string;
  accountNumber?: string;
  sanctionedAmount?: number;
  outstandingAmount?: number;
  emiAmount?: number;
  nextEmiDate?: string;
  tenureRemaining?: number; // months
  interestRate?: number;
};

export type Vehicle = {
  id: string;
  rc: RcData;
  loan: LoanData;
  insuranceAA: InsuranceAAData;
};

export type DrivingLicense = {
  dl_number: string;
  holder_name: string;
  date_of_birth: string;
  issue_date: string;
  expiry_date: string;
  blood_group?: string;
  vehicle_classes: string[];
  issuing_authority: string;
};

export type DigiLockerStatus = {
  connected: boolean;
  connectedAt?: string;
  documents: string[];
};

export type NotificationPrefs = {
  whatsapp: boolean;
  sms: boolean;
  email: boolean;
  push: boolean;
  leadDays: number[]; // e.g. [30, 15, 7, 1]
};

export const defaultNotificationPrefs: NotificationPrefs = {
  whatsapp: true,
  sms: true,
  email: true,
  push: true,
  leadDays: [30, 15, 7, 1],
};

export const mockDigiLocker: DigiLockerStatus = {
  connected: false,
  documents: [],
};

export const mockDL: DrivingLicense = {
  dl_number: 'GJ0120230012345',
  holder_name: 'JOHN DOE',
  date_of_birth: '1988-04-12',
  issue_date: '2019-06-15',
  expiry_date: '2039-06-14',
  blood_group: 'B+',
  vehicle_classes: ['LMV', 'MCWG'],
  issuing_authority: 'RTO AHMEDABAD',
};

export const mockVehicles: Vehicle[] = [
  {
    id: 'v1',
    rc: {
      document_type: 'RC',
      document_id: 'GJ27EF6223',
      owner_data: {
        serial: '1',
        name: 'JOHN DOE',
        name_match_score_1: '100.0',
        name_match_score_2: '100.0',
        present_address:
          '58 MADHAVRAJ BUNGLOWS,BEHIND RELIANCE PETROL PUMP,S P RING ROAD VASTRAL,AHMEDABAD,GUJARAT',
        permanent_address:
          '58 MADHAVRAJ BUNGLOWS,BEHIND RELIANCE PETROL PUMP,S P RING ROAD VASTRAL,AHMEDABAD,GUJARAT',
      },
      issue_date: '2025-01-31',
      expiry_date: '2040-01-30',
      registered_at: 'AHMEDABAD EAST, GUJARAT',
      status: 'ACTIVE',
      norms_type: 'BHARAT STAGE VI',
      tax_end_date: '2040-01-30',
      financier: 'HDFC BANK LTD',
      financed: true,
      is_commercial: false,
      pucc_data: {
        pucc_number: 'Newv4',
        expiry_date: '2026-01-30',
      },
      insurance_data: {
        policy_number: 'D185007673',
        company: 'GODIGIT GENERAL INSURANCE LTD.',
        expiry_date: '2028-01-30',
      },
      vehicle_data: {
        manufactured_date: '2025-01',
        category: 'LMV',
        category_description: 'Motor Car(LMV)',
        chassis_number: 'MA1NE2ZTFS6A40709',
        engine_number: 'ZTR4M79080',
        maker_description: 'MAHINDRA & MAHINDRA LIMITED',
        maker_model: 'XUV700 AX7 DSL AT 7 SEATER',
        body_type: 'HARD TOP',
        fuel_type: 'DIESEL',
        color: 'C1MDNGTBLK',
        cubic_capacity: '2184.00',
        gross_weight: '2394',
        number_of_cylinders: '4',
        seating_capacity: '7',
        sleeper_capacity: '0',
        standing_capacity: '0',
        wheelbase: '2750',
        unladen_weight: '1788',
      },
    },
    loan: {
      linked: true,
      lender: 'HDFC BANK LTD',
      accountNumber: 'XXXXXXX3429',
      sanctionedAmount: 1850000,
      outstandingAmount: 1612400,
      emiAmount: 31250,
      nextEmiDate: '2026-06-05',
      tenureRemaining: 78,
      interestRate: 9.4,
    },
    insuranceAA: {
      linked: true,
      premium: 38420,
      idv: 1745000,
      ncb: 20,
      addOns: ['Zero Depreciation', 'Engine Protect', 'Roadside Assistance'],
      lastClaim: {
        date: '2025-08-12',
        amount: 42500,
        status: 'SETTLED',
      },
      claimSettlementRatio: 89,
    },
  },
  {
    id: 'v2',
    rc: {
      document_type: 'RC',
      document_id: 'GJ01XP2059',
      owner_data: {
        serial: '1',
        name: 'JANE DOE',
        name_match_score_1: '25.0',
        name_match_score_2: '33.33',
        present_address:
          'F-304,INDRAPRASTH GREEN,SHYAMAL CROSS ROAD,OPP SHIKHAR TOWER,AHMADABAD CITY,380015',
        permanent_address:
          'F-304,INDRAPRASTH GREEN,SHYAMAL CROSS ROAD,OPP SHIKHAR TOWER,AHMADABAD CITY,380015',
      },
      issue_date: '2023-12-25',
      expiry_date: '2038-12-24',
      registered_at: 'AHMEDABAD, GUJARAT',
      status: 'ACTIVE',
      norms_type: 'BHARAT STAGE VI',
      tax_end_date: '2038-12-24',
      financed: false,
      is_commercial: false,
      pucc_data: {
        pucc_number: 'GJ00101060221874',
        expiry_date: '2026-06-16',
      },
      insurance_data: {
        policy_number: 'D129285146',
        company: 'GODIGIT GENERAL INSURANCE LTD.',
        expiry_date: '2026-05-30',
      },
      vehicle_data: {
        manufactured_date: '2023-11',
        category: '2WN',
        category_description: 'M-Cycle/Scooter(2WN)',
        chassis_number: 'MD626AK44P1N30000',
        engine_number: 'BK4NP1X28120',
        maker_description: 'TVS MOTOR COMPANY LTD',
        maker_model: 'TVS JUPITER 125',
        body_type: 'SOLO WITH PILLION',
        fuel_type: 'PETROL',
        color: 'C BRONZE',
        cubic_capacity: '124.76',
        gross_weight: '238',
        number_of_cylinders: '1',
        seating_capacity: '2',
        sleeper_capacity: '0',
        standing_capacity: '0',
        wheelbase: '1275',
        unladen_weight: '108',
      },
    },
    loan: {
      linked: false,
    },
    insuranceAA: {
      linked: false,
    },
  },
  {
    id: 'v3',
    rc: {
      document_type: 'RC',
      document_id: 'MH01EB0001',
      owner_data: {
        serial: '1',
        name: 'ACME CORPORATION LIMITED',
        name_match_score_1: '14.81',
        name_match_score_2: '0.0',
        present_address:
          'MAKER CHEMBER IV,3RD FLOOR,NARIMAN POINT,MUMBAI,MUMBAI,MAHARASHTRA',
        permanent_address:
          'MAKER CHEMBER IV,3RD FLOOR,NARIMAN POINT,MUMBAI,MUMBAI,MAHARASHTRA',
      },
      issue_date: '2022-01-31',
      expiry_date: '2037-01-30',
      registered_at: 'MUMBAI (CENTRAL), MAHARASHTRA',
      status: 'ACTIVE',
      norms_type: 'BHARAT STAGE VI',
      tax_end_date: '2037-01-30',
      financed: false,
      is_commercial: false,
      pucc_data: {
        pucc_number: 'MH04802320066958',
        expiry_date: '2026-03-06',
      },
      insurance_data: {
        policy_number: '2302204447347902000',
        company: 'HDFC ERGO GENERAL INSURANCE COMPANY LTD',
        expiry_date: '2026-12-28',
      },
      vehicle_data: {
        manufactured_date: '2021-11',
        category: 'LMV',
        category_description: 'Motor Car(LMV)',
        chassis_number: 'SCATF2200NU10027',
        engine_number: '90244106',
        maker_description: 'ROLLS ROYCE',
        maker_model: 'ROLLS-ROYCE CULLINAN',
        body_type: 'SUV',
        fuel_type: 'PETROL',
        color: 'TUSCAN SUN',
        cubic_capacity: '6750.00',
        gross_weight: '0',
        number_of_cylinders: '12',
        seating_capacity: '5',
        sleeper_capacity: '0',
        standing_capacity: '0',
        wheelbase: '0',
        unladen_weight: '2660',
      },
    },
    loan: {
      linked: false,
    },
    insuranceAA: {
      linked: false,
    },
  },
];

// Insurer catalogue for compare-&-buy flow (Riskcovry / Turtlemint mocked)
export type InsurerQuote = {
  insurer: string;
  premium: number;
  idv: number;
  addons: string[];
  cashlessGarages: number;
  claimRatio: number;
  logoBg: string;
};

export const mockInsurerQuotes: InsurerQuote[] = [
  {
    insurer: 'ACKO',
    premium: 4280,
    idv: 425000,
    addons: ['Zero Dep', 'Engine Protect'],
    cashlessGarages: 1800,
    claimRatio: 87,
    logoBg: '#1a73e8',
  },
  {
    insurer: 'GoDigit',
    premium: 4540,
    idv: 432000,
    addons: ['Zero Dep', 'RTI', 'Engine Protect'],
    cashlessGarages: 5500,
    claimRatio: 89,
    logoBg: '#7c2d12',
  },
  {
    insurer: 'HDFC ERGO',
    premium: 5120,
    idv: 450000,
    addons: ['Zero Dep', 'NCB Protect', 'RTI'],
    cashlessGarages: 8200,
    claimRatio: 91,
    logoBg: '#dc2626',
  },
  {
    insurer: 'ICICI Lombard',
    premium: 5680,
    idv: 460000,
    addons: ['Zero Dep', 'Engine Protect', 'Tyre Cover'],
    cashlessGarages: 7100,
    claimRatio: 90,
    logoBg: '#f97316',
  },
];

// Deeplinks used for renewal CTAs (these are public hosted-renewal endpoints)
export const insurerDeeplinks: Record<string, string> = {
  'GODIGIT GENERAL INSURANCE LTD.': 'https://www.godigit.com/renew-motor-policy',
  'HDFC ERGO GENERAL INSURANCE COMPANY LTD':
    'https://www.hdfcergo.com/renewals/car-insurance',
  'ACKO GENERAL INSURANCE LTD':
    'https://www.acko.com/car-insurance/renew/',
  'ICICI LOMBARD GENERAL INSURANCE COMPANY LIMITED':
    'https://www.icicilombard.com/motor-insurance/renew',
};

export const daysBetween = (target: string): number => {
  const t = new Date(target).getTime();
  const now = new Date().getTime();
  return Math.ceil((t - now) / (1000 * 60 * 60 * 24));
};

export const expiryStatus = (
  days: number
): { label: string; tone: 'good' | 'warn' | 'bad' } => {
  if (days < 0) return { label: 'Expired', tone: 'bad' };
  if (days <= 30) return { label: 'Expiring soon', tone: 'bad' };
  if (days <= 90) return { label: 'Renew soon', tone: 'warn' };
  return { label: 'Active', tone: 'good' };
};

export const formatDate = (d: string) => {
  const date = new Date(d);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const formatInr = (n: number) =>
  '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

export const computeHealthScore = (v: Vehicle): number => {
  const items = [
    v.rc.expiry_date,
    v.rc.insurance_data.expiry_date,
    v.rc.pucc_data.expiry_date,
    v.rc.tax_end_date,
  ];
  let score = 100;
  items.forEach((d) => {
    const days = daysBetween(d);
    if (days < 0) score -= 25;
    else if (days <= 30) score -= 12;
    else if (days <= 90) score -= 5;
  });
  return Math.max(0, Math.min(100, score));
};

export const vehicleAgeMonths = (manufactured: string): number => {
  const [y, m] = manufactured.split('-').map(Number);
  const made = new Date(y, (m ?? 1) - 1, 1).getTime();
  const months = Math.floor(
    (Date.now() - made) / (1000 * 60 * 60 * 24 * 30.4375)
  );
  return Math.max(0, months);
};
